import { ITrialClass, ITutor } from '@wot/typings';
import { DEFAULT_PROFILE_PIC, GREEN_ARROW_UP, RED_ARROW_DOWN } from '../../../../assets/icons';
import { useState, useEffect } from 'react';
import { currencyFormatter, getAttendacePercentage, increasedPrecentage } from '../../../../utils/functions';
import { ClassItem } from '../../../Student-Management/Manage/Calendar';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AttendanceGuage, Button } from '@wot/components';
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';
import Slider from '../../../../components/Slider';
import tutorsApi from '../../../../api/tutors';
import xhr from '../../../../utils/fetch';
import { addDays, compareAsc, endOfMonth, endOfWeek, parseISO, startOfMonth, startOfWeek, subMonths } from 'date-fns';
import { format } from 'date-fns/esm';
import { TutorPlan } from '../../../../interfaces/Plan';
import { TableBody, TableHeading, TableHeadingCol, TableRow } from '../../../../components/Table';
import { TableRowItem } from '../components/TableRowItem';
import BreadCrumb from '../../../../components/BreadCrumb';
import { IoMdArrowBack } from 'react-icons/io';

interface ICat {
  name: string[];
}

export interface Student {
  _id: string;
  category: ICat;
  categoryType: string;
  user: {
    _id: string;
    email: string;
    profilePic: string;
    name: string;
    phoneNumber: string;
    timezone: string;
    createdAt: string;
    updatedAt: string;
  };
}

export default function TutorDashboard() {
  const [currentPath, setCurrentPath] = useState('dashboard');
  const { tutor_id } = useParams();
  const [profile, setProfile] = useState<ITutor>();
  const [upcomingSessions, setUpcomingSessions] = useState<ITrialClass[]>([]);
  const [totalSessions, setTotalSessions] = useState<ITrialClass[]>([]);
  const [range] = useState('This Week');
  const [actualEarnings, setActualEarnings] = useState(0);
  const [lastMonthActualEarnings, setLastMonthActualEarnings] = useState(0);
  const [currentMonthDeductions, setCurrentMonthDeductions] = useState(0);
  const [lastMonthDeductions, setLastMonthDeductions] = useState(0);
  const [currentPlansCount, setCurrentPlansCount] = useState(0);
  const [, setLastMonthPlansCount] = useState(0);
  const [studentsCount, setStudentsCount] = useState({
    current: 0,
    lastMonth: 0
  });

  const [demoClasses, setDemoClasses] = useState({
    current: 0,
    lastMonth: 0
  });

  const [currentMonth] = useState(new Date());
  const [chartData, setChartData] = useState<{ label: string; Earnings: number; Expenses: number }[]>([]);
  const [activePlans, setActivePlans] = useState<TutorPlan[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (profile) {
      let paths = location.pathname.split('/');
      if (paths[paths.length - 1] === profile?._id) {
        setCurrentPath('dashboard');
      } else {
        setCurrentPath(paths[paths.length - 1]);
      }
    }
  }, [location.pathname, profile]);

  // Get Tutor Profile
  useEffect(() => {
    if (!tutor_id) {
      return;
    }
    tutorsApi
      .getTutorById(tutor_id)
      .then((data) => {
        if (data) {
          setProfile(data);
        }
      })
      .catch((err) => err);
  }, []);

  // Upcoming Sessions
  useEffect(() => {
    if (profile?._id) {
      xhr({
        url: `/t/${profile?._id}/classes`,
        method: 'GET'
      }).then((res) => {
        if (res.status === 'OK') {
          let sortedSlots = res.data.sort((a: any, b: any) =>
            compareAsc(new Date(a.scheduledTime.start), new Date(b.scheduledTime.start))
          );
          setUpcomingSessions(sortedSlots.filter((item: any) => item.status === 'upcoming'));
        }
      });
    }
  }, [profile]);

  // Total Sessions
  useEffect(() => {
    if (profile?._id) {
      xhr({
        url: `/t/${profile?._id}/sessions`,
        method: 'GET',
        params: {
          type: 'plan',
          status: 'completed'
        }
      })
        .then((res) => {
          if (res.status === 'OK') {
            setTotalSessions(res.data);
          }
        })
        .catch(() => { });
    }
  }, [profile]);

  // Actual Earnings And Last month earnings
  useEffect(() => {
    getEarnings(startOfMonth(currentMonth).toString(), endOfMonth(currentMonth).toString()).then((data) => {
      if (data) {
        setActualEarnings(data.reduce((acc: any, curr: any) => (acc += curr.total), 0));
      }
    });
    getEarnings(
      startOfMonth(subMonths(currentMonth, 1)).toString(),
      endOfMonth(subMonths(currentMonth, 1)).toString()
    ).then((data) => {
      if (data) {
        setLastMonthActualEarnings(data.reduce((acc: any, curr: any) => (acc += curr.total), 0));
      }
    });
    getDeductions(startOfMonth(currentMonth).toString(), endOfMonth(currentMonth).toString()).then((data) => {
      if (data) {
        setCurrentMonthDeductions(data.reduce((acc: any, curr: any) => (acc += curr.total), 0));
      }
    });
    getDeductions(
      startOfMonth(subMonths(currentMonth, 1)).toString(),
      endOfMonth(subMonths(currentMonth, 1)).toString()
    ).then((data) => {
      if (data) {
        setLastMonthDeductions(data.reduce((acc: any, curr: any) => (acc += curr.total), 0));
      }
    });
  }, [profile]);

  const getEarnings = async (start: string, end: string) => {
    return await xhr({
      url: `/t/${profile?._id}/earnings?startAfter=${start}&endBefore=${end}`,
      method: 'GET'
    })
      .then((res) => {
        if (res.status === 'OK') {
          return res.data;
        } else {
          return null;
        }
      })
      .catch((err) => err);
  };

  const getDeductions = async (start: string, end: string) => {
    return await xhr({
      url: `/t/${profile?._id}/deductions?startAfter=${start}&endBefore=${end}`,
      method: 'GET'
    })
      .then((res) => {
        if (res.status === 'OK') {
          return res.data;
        } else {
          return null;
        }
      })
      .catch((err) => err);
  };

  const getPlansCount = async (start: string, end: string) => {
    if (profile?._id) {
      return await xhr({
        url: `/t/${profile?._id}/stats/plans-count?startAfter=${start}&endBefore=${end}`,
        method: 'GET'
      })
        .then((res) => {
          if (res.status === 'OK') {
            return res.data;
          } else {
            return null;
          }
        })
        .catch((err) => err);
    }
  };

  useEffect(() => {
    if (range === 'This Week') {
      let start = startOfWeek(new Date()).toString();
      let end = endOfWeek(new Date()).toString();
      getEarnings(start, end).then((res) => {
        let temp: any = [];
        let weekDay = startOfWeek(new Date());
        [...new Array(7)].map((_, i) => {
          let day = format(addDays(weekDay, i), 'EEE');
          let date = format(addDays(weekDay, i), 'd');
          const v = res.find((item: any) => format(parseISO(item.date), 'd') === date);
          temp.push({
            label: day,
            Earnings: (v && v.total) || 0,
            Expenses: 0
          });
        });
        setChartData(temp);
      });
    }
  }, [profile]);

  useEffect(() => {
    getDemoClasses().then((data) => data && setDemoClasses({ current: data.length, lastMonth: 0 }));
    getStudentsCount(startOfMonth(currentMonth).toString(), endOfMonth(currentMonth).toString()).then(
      (data) => data && setStudentsCount({ current: data.count, lastMonth: data.previousCount })
    );
    getPlansCount(startOfMonth(currentMonth).toString(), endOfMonth(currentMonth).toString()).then((data) => {
      if (data) {
        setCurrentPlansCount(data.count);
      }
    });
    getPlansCount(
      startOfMonth(subMonths(currentMonth, 1)).toString(),
      endOfMonth(subMonths(currentMonth, 1)).toString()
    ).then((data) => {
      if (data) {
        setLastMonthPlansCount(data.count);
      }
    });
  }, [profile]);

  const getDemoClasses = async () => {
    if (profile?._id) {
      return await xhr({
        url: `/t/${profile?._id}/sessions`,
        method: 'GET',
        params: {
          type: 'trial'
        }
      })
        .then((res) => {
          if (res.status === 'OK') {
            return res.data;
          }
        })
        .catch(() => { });
    }
  };

  const getStudentsCount = async (start: string, end: string) => {
    if (profile?._id) {
      return await xhr({
        url: `/t/${profile?._id}/stats/students-count/?startAfter=${start}&endBefore=${end}`,
        method: 'GET'
      })
        .then((res) => {
          if (res.status === 'OK') {
            return res.data;
          }
        })
        .catch(() => { });
    }
  };

  useEffect(() => {
    if (profile?._id) {
      xhr({
        url: `/t/${profile?._id}/plans`,
        method: 'GET'
      }).then((res) => {
        if (res.status === 'OK') {
          let temp = JSON.parse(JSON.stringify(res.data));
          let sortedPlans = temp.sort((a: any, b: any) => compareAsc(new Date(a.createdAt), new Date(b.createdAt)));
          let active = sortedPlans.filter((item: any) => item.status === 'active');
          setActivePlans(active);
        }
      });
    }
  }, [profile]);

  if (!profile?._id) {
    return null;
  }

  return (
    <div className="w-full h-full flex flex-col  gap-4 bg-neutralBlue2 p-4">
      <BreadCrumb />
      <div className="w-full flex flex-col my-2 grow">
        <div className='flex items-center my-2 gap-2'>
          <IoMdArrowBack className='text-xl cursor-pointer' onClick={() => navigate(-1)} />
          <h2 className="text-xl font-bold text-black">
            {`${profile?.personal?.data?.name}'s `}
            <span className="capitalize">{currentPath}</span>
          </h2>
        </div>
        {currentPath === 'dashboard' ? (
          <div className="w-full h-full flex flex-col gap-5 py-2 px-2">
            <table className="w-full h-full table-auto">
              <TableHeading customClass="bg-neutralBlue2 py-4">
                <TableRow>
                  <TableHeadingCol label="Date" />
                  <TableHeadingCol label="Tutor ID" />
                  <TableHeadingCol label="NAME" />
                  <TableHeadingCol label="EMAIL" />
                  <TableHeadingCol label="PHONE NO." />
                  <TableHeadingCol label="CATEGORY" />
                  <TableHeadingCol label="STATUS" />
                </TableRow>
              </TableHeading>
              <TableBody>
                <>
                  <TableRow>
                    <>
                      <TableRowItem tutor={profile} setTutor={setProfile} />
                    </>
                  </TableRow>
                </>
              </TableBody>
            </table>
            <div className="w-full h-full grid grid-cols-7 gap-8">
              {/* <div className="flex flex-col md:flex-row md:flex-auto gap-8"> */}
              <div className="col-span-2  w-full flex flex-col gap-8">
                <div className="px-6 py-4 w-full min-w-[256px] max-h-[400px] border flex flex-col gap-2 justify-center border-gray-300 rounded-xl bg-white items-center">
                  <img
                    src={profile?.personal?.data?.profilePic || DEFAULT_PROFILE_PIC}
                    alt="Profile Picture"
                    className="h-20 w-20 object-cover rounded-full"
                  />
                  <h2 className="text-lg font-medium p-2">{profile?.personal?.data?.name}</h2>
                  <div className="flex justify-center divide-x">
                    <div className="flex flex-col items-center px-1">
                      <span className="text-sm font-medium">{studentsCount?.current || 0}</span>
                      <span className="text-xs font-medium text-neutralGray2">Students</span>
                    </div>
                    <div className="flex flex-col items-center px-1">
                      <span className="text-sm font-medium">
                        {getAttendacePercentage(
                          totalSessions.filter((i) => i.attendance && i.attendance.student === 'present').length,
                          totalSessions.length
                        )}%
                      </span>
                      <span className="text-xs font-medium text-neutralGray2">Attendance</span>
                    </div>
                  </div>
                  <div className="flex justify-center flex-wrap py-2">
                    {profile?.profession?.data.categories.map((item: any, i: any) => (
                      <span
                        className="rounded-full border bg-neutralBlue2 text-primary text-xs font-medium px-3 py-1 mx-1 mt-2 group-hover:bg-white"
                        key={i}
                      >
                        {item.name}
                      </span>
                    ))}
                  </div>
                  <Link to={'profile'} className="my-4">
                    <Button text="View Profile" type="secondary" />
                  </Link>
                  {/* {!profile?._id && (
                <>
                  <div className="w-full">
                    <div className="flex flex-row items-center justify-between pb-2 pt-8">
                      <p>Profile Completion</p>
                      <p>{profileProgress}%</p>
                    </div>
                    <div className="relative rounded-full w-full bg-neutralBlue1 h-2">
                      <div
                        style={{ width: `${profileProgress}%` }}
                        className={`absolute rounded-full bg-primary h-2`}
                      ></div>
                    </div>
                  </div>
                  {profileProgress < 80 && (
                    <div className="w-40 pt-20 pb-10">
                      <Button text="Complete Profile" onClick={() => { }} />
                    </div>
                  )}
                </>
              )} */}
                </div>

                <div className="h-full gap-8 overflow-y-scroll custom-scrollbar">
                  <CardContainer
                    title="Upcoming Sessions"
                    link="calendar"
                    content={
                      upcomingSessions.length > 0 ? (
                        <div className="flex flex-col gap-4">
                          {upcomingSessions.slice(0, 3).map((session) => (
                            <ClassItem key={session._id} session={session} />
                          ))}
                        </div>
                      ) : null
                    }
                    placeHolder={
                      <div className="flex flex-col gap-5 items-center">
                        <p className="text-sm">No Classes found.</p>
                      </div>
                    }
                  />
                </div>
              </div>

              <div className="col-span-5 flex flex-col gap-4 w-full min-w-[256px] rounded-xl items-center">
                <div>
                  <CardContainer
                    title="Earnings"
                    link="earnings"
                    content={
                      <div className="w-full gap-4 items-stretch grid grid-cols-5">
                        <div className="col-span-3 grow flex flex-col p-8 gap-5 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
                          <div className="flex justify-between gap-2">
                            <h1 className="text-2xl font-bold">
                              INR {chartData.reduce((acc, currValue) => acc + currValue.Earnings, 0).toFixed(2)}
                            </h1>
                            <h1 className="text-base font-normal">This Week</h1>
                          </div>

                          <div className="w-full h-[164px] mt-4">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                width={730}
                                height={164}
                                data={chartData}
                                margin={{
                                  top: 5,
                                  right: 30,
                                  left: 20,
                                  bottom: 5
                                }}
                              >
                                <CartesianGrid vertical={false} strokeDasharray="1" />
                                <XAxis dataKey="label" fontSize={12} />
                                <YAxis axisLine={false} width={20} fontSize={12} orientation="right" />
                                <Tooltip />
                                <Bar dataKey="Earnings" fill="#8884d8" barSize={10}>
                                  {chartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill="#EA4459" strokeLinecap="round" />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                        {/* <div className='w-full flex flex-wrap gap-4'> */}
                        <div className=" flex flex-col gap-7 p-8 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
                          <h3 className="text-base font-semibold">Earnings this month</h3>
                          <h1 className="text-2xl">INR {currencyFormatter(actualEarnings)}</h1>
                          <div className="flex items-center gap-2">
                            {lastMonthActualEarnings > actualEarnings ? (
                              <>
                                <p className="text-red-500 text-sm">
                                  {increasedPrecentage(lastMonthActualEarnings, actualEarnings)}%
                                </p>
                                <img src={RED_ARROW_DOWN} className="w-6 h-6" />
                              </>
                            ) : (
                              <>
                                <p className="text-green-500 text-sm">
                                  {increasedPrecentage(lastMonthActualEarnings, actualEarnings)}%
                                </p>
                                <img src={GREEN_ARROW_UP} className="w-6 h-6" />
                              </>
                            )}
                          </div>
                        </div>

                        <div className=" flex flex-col gap-7 p-8 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
                          <h3 className="text-base font-semibold">Deductions this month</h3>
                          <h1 className="text-2xl">INR {currencyFormatter(currentMonthDeductions)}</h1>
                          <div className="flex items-center gap-2">
                            {lastMonthDeductions > currentMonthDeductions ? (
                              <>
                                <p className="text-green-500 text-sm">
                                  {increasedPrecentage(lastMonthDeductions, currentMonthDeductions)}%
                                </p>
                                <img src={GREEN_ARROW_UP} className="w-6 h-6" />
                              </>
                            ) : (
                              <>
                                <p className="text-red-500 text-sm">
                                  {increasedPrecentage(lastMonthDeductions, currentMonthDeductions)}%
                                </p>
                                <img src={RED_ARROW_DOWN} className="w-6 h-6" />
                              </>
                            )}
                          </div>
                        </div>
                        {/* </div> */}
                      </div>
                    }
                    placeHolder={
                      <div className="flex flex-col gap-5 items-center">
                        <p className="text-sm">Your data related will show up here once you start taking classes.</p>
                      </div>
                    }
                  />
                </div>
                <div className="w-full grid grid-cols-8 gap-4">
                  <div className="col-span-5">
                    <CardContainer
                      title="Analytics overview"
                      content={
                        <div className="w-full flex gap-4 justify-between">
                          <div className="shrink-0 grow basis-1 flex flex-col gap-7 p-4 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
                            <h3 className="text-base font-semibold">Bookings</h3>
                            <div className="flex justify-between">
                              <h1 className="text-2xl">{currentPlansCount}</h1>
                              {/* <div className="flex items-center gap-2">
                            {lastMonthDeductions > currentMonthDeductions ? (
                              <>
                                <p className="text-red-500 text-sm">
                                  {increasedPrecentage(lastMonthDeductions, currentMonthDeductions)}%
                                </p>
                                <img src={RED_ARROW_DOWN} className="w-6 h-6" />
                              </>
                            ) : (
                              <>
                                <p className="text-green-500 text-sm">
                                  {increasedPrecentage(lastMonthDeductions, currentMonthDeductions)}%
                                </p>
                                <img src={GREEN_ARROW_UP} className="w-6 h-6" />
                              </>
                            )}
                          </div> */}
                            </div>
                          </div>
                          <div className="shrink-0 grow basis-1 flex flex-col gap-7 p-4 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
                            <h3 className="text-base font-semibold">Students</h3>
                            <div className="flex justify-between">
                              <h1 className="text-2xl">{studentsCount.current}</h1>
                              {/* <div className="flex items-center gap-2">
                            {studentsCount.lastMonth > studentsCount.current ? (
                              <>
                                <p className="text-red-500 text-sm">
                                  {increasedPrecentage(studentsCount.lastMonth, studentsCount.current)}%
                                </p>
                                <img src={RED_ARROW_DOWN} className="w-6 h-6" />
                              </>
                            ) : (
                              <>
                                <p className="text-green-500 text-sm">
                                  {increasedPrecentage(studentsCount.lastMonth, studentsCount.current)}%
                                </p>
                                <img src={GREEN_ARROW_UP} className="w-6 h-6" />
                              </>
                            )}
                          </div> */}
                            </div>
                          </div>
                          <div className="shrink-0 grow basis-1 flex flex-col gap-7 p-4 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
                            <h3 className="text-base font-semibold">Demo Classes</h3>
                            <div className="flex justify-between">
                              <h1 className="text-2xl">{demoClasses.current}</h1>
                              {/* <div className="flex items-center gap-2">
                            {demoClasses.lastMonth > demoClasses.current ? (
                              <>
                                <p className="text-red-500 text-sm">
                                  {increasedPrecentage(demoClasses.lastMonth, demoClasses.current)}%
                                </p>
                                <img src={RED_ARROW_DOWN} className="w-6 h-6" />
                              </>
                            ) : (
                              <>
                                <p className="text-green-500 text-sm">
                                  {increasedPrecentage(demoClasses.lastMonth, demoClasses.current)}%
                                </p>
                                <img src={GREEN_ARROW_UP} className="w-6 h-6" />
                              </>
                            )}
                          </div> */}
                            </div>
                          </div>
                        </div>
                      }
                      placeHolder={
                        <div className="flex flex-col gap-5 items-center">
                          <p className="text-sm">
                            Your overall reports will show up here once a student books a plan with you
                          </p>
                        </div>
                      }
                    />
                  </div>
                  <div className="col-span-3">
                    <CardContainer
                      title="My Attendance"
                      link="attendance"
                      content={
                        <>
                          {totalSessions ? (
                            <div className="w-full  max-h-[256px] flex flex-col items-center bg-white rounded-xl p-4">
                              <div className="w-[40%] md:w-[50%] text-2xl grow flex items-center justify-center p-4">
                                <AttendanceGuage
                                  percentage={getAttendacePercentage(
                                    totalSessions.filter((i) => i.attendance && i.attendance.student === 'present')
                                      .length,
                                    totalSessions.length
                                  )}
                                />
                              </div>
                            </div>
                          ) : null}
                        </>
                      }
                      placeHolder={
                        <p className="font-medium">
                          Your attendance stats will show up here once you start taking classes
                        </p>
                      }
                    />
                  </div>
                </div>

                <div className="w-full grid grid-cols-2 gap-4">
                  <ActivePlans activePlans={activePlans} />
                  <Students tutorId={profile._id} />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <Outlet />
        )}
      </div>
    </div>
  );
}

interface Props {
  title: string;
  content?: any;
  link?: string;
  placeHolder?: any;
}

export function CardContainer({ title, content, link, placeHolder }: Props) {
  return (
    <div className="w-full h-full bg-white border flex flex-col p-5 rounded-2xl gap-6  min-h-[256px] lg:drop-shadow-md">
      <div className="flex flex-row justify-between items-center">
        <p className="text-lg font-extrabold">{title}</p>
        {link && (
          <Link to={link}>
            <p className="text-sm font-medium text-black cursor-pointer hover:text-black/80">View All</p>
          </Link>
        )}
      </div>
      <div className="w-full h-full">{content ? content : placeHolder}</div>
    </div>
  );
}

const ActivePlans = ({ activePlans }: { activePlans: TutorPlan[] }) => {
  return (
    <div className="rounded-2xl bg-white border border-neutralGray3 p-5 w-full h-full flex flex-col items-center gap-3">
      <div className="w-full flex flex-row justify-between items-center">
        <span className="text-base font-medium">Active Plans ({activePlans.length})</span>
        <Link to={'plans'}>
          <span className="text-sm font-normal text-black/80 cursor-pointer hover:text-primary hover:underline hover:underline-offset-1">
            View All
          </span>
        </Link>
      </div>
      {activePlans.slice(0, 2).map((plan) => (
        <div key={plan._id} className="w-full border border-primary rounded-lg">
          <div className="border-l-4 border-primary rounded-lg">
            <div className="px-6 py-4 border-b-2 border-neutralBlue1">
              <span className="flex flex-col gap-1">
                <p className="text-xl font-medium">{plan.user.name}</p>
                <p className="text-base text-neutralGray2 font-normal"> {plan.totalSessions} Classes / 1 hour</p>
              </span>
            </div>
            <div className="px-6 py-4 flex flex-col gap-2">
              <span className="flex justify-between text-base text-neutralGray2 font-normal">
                <p>Scheduled Lessons</p>
                <p>
                  {plan.sessions?.length || 0}/{plan.totalSessions}
                </p>
              </span>
              <div className="relative w-full h-2 bg-neutralBlue1 rounded-full">
                <div
                  style={{
                    width: `${plan.sessions ? (plan.sessions?.length / plan.totalSessions) * 100 : 0}%`
                  }}
                  className="absolute bg-primary w-full h-full top-0 left-0 rounded-full"
                ></div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const Students = ({ tutorId }: { tutorId: string }) => {
  const [students, setStudents] = useState<Student[]>([]);

  const getTutorStudents = () => {
    if (tutorId) {
      xhr({
        url: `/t/students`,
        method: 'GET',
        params: {
          _id: tutorId
        }
      }).then((res) => {
        if (res.status === 'OK') {
          let tempIds: string[] = [];
          let temp: Student[] = [];
          res.data.forEach((item: any) => {
            if (!tempIds.includes(item.user.email)) {
              temp.push(item);
              tempIds.push(item.user.email);
            }
          });
          setStudents(temp);
        }
      });
    }
  };

  useEffect(() => {
    getTutorStudents();
  }, [tutorId]);

  return (
    <div className="rounded-2xl bg-white border border-neutralGray3 p-5 w-full h-full flex flex-col items-center gap-3">
      <div className="w-full flex flex-row justify-between items-center">
        <span className="text-base font-medium">Students</span>
        <Link to={'students'}>
          <span className="text-sm font-normal text-black/80 cursor-pointer hover:text-primary hover:underline hover:underline-offset-1">
            View All
          </span>
        </Link>
      </div>
      {students.length > 0 && (
        <div className="w-full h-full">
          <Slider>
            {students.map((student) => (
              <StudentCard key={student._id} student={student} />
            ))}
          </Slider>
        </div>
      )}
    </div>
  );
};

export function StudentCard({ student }: { student: Student }) {
  const navigate = useNavigate();
  return (
    <div className="w-full h-full md:shrink-0 flex flex-col border border-neutralGray3 p-4 rounded-lg cursor-pointer hover:bg-neutralBlue2">
      <div className="h-full flex flex-col gap-4 items-start md:items-center md:px-10 py-4">
        <div className="flex md:flex-col items-center gap-2">
          <div className="h-10 w-10 md:h-16 md:w-16 rounded-full overflow-hidden">
            <img
              src={student.user.profilePic || DEFAULT_PROFILE_PIC}
              alt="tutor_profile_pic"
              className="w-full h-full object-cover"
            />
          </div>
          <span className="text-2xl font-normal">{student.user.name}</span>
          <div className="flex flex-wrap">
            {student.category.name.map((course, i) => (
              <div key={i} className="bg-neutralBlue2 px-3 py-1 text-sm rounded-full">
                {course}
              </div>
            ))}
          </div>
        </div>
        <div className="w-full max-w-sm mx-auto mt-auto flex flex-col gap-4 items-center justify-center whitespace-nowrap">
          <Button
            onClick={() => navigate(`/student-management/${student.user._id}`)}
            block
            text="View Profile"
            type="secondary"
            size="sm"
          />
        </div>
      </div>
    </div>
  );
}
